import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { BarChart3, Eye, Package, Star } from "lucide-react";

/* ---- Helpers ---- */

const KeyMetricCard = ({ Icon, iconClass, value, label }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="text-center">
        <Icon className={`w-8 h-8 mx-auto mb-2 ${iconClass}`} />
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-sm text-gray-600">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const StatusBadges = ({ data }) => (
  <div className="flex gap-2 flex-wrap">
    {data.is_trending && <Badge className="bg-orange-500 text-white">🔥 Trending</Badge>}
    {data.is_popular && <Badge className="bg-purple-500 text-white">⭐ Popolare</Badge>}
    {data.published_date && <Badge variant="outline">📅 Pubblicato: {data.published_date}</Badge>}
  </div>
);

const ViewsChart = ({ data }) => {
  const maxViews = Math.max(...data.map((d) => d.views), 1);
  const width = 100;
  const height = 100;
  const points = data
    .map((d, i) => {
      const x = (i / (data.length - 1 || 1)) * width;
      const y = height - (d.views / maxViews) * height;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Visualizzazioni Ultimi 30 Giorni</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64 relative">
          <div className="w-full h-full">
            <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
              <line x1="0" y1="25" x2="100" y2="25" stroke="#e5e7eb" strokeWidth="0.2" />
              <line x1="0" y1="50" x2="100" y2="50" stroke="#e5e7eb" strokeWidth="0.2" />
              <line x1="0" y1="75" x2="100" y2="75" stroke="#e5e7eb" strokeWidth="0.2" />
              <polyline points={points} fill="none" stroke="#8b5cf6" strokeWidth="2" vectorEffect="non-scaling-stroke" />
              <polygon points={`0,${height} ${points} ${width},${height}`} fill="url(#gradient)" opacity="0.3" />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#8b5cf6" />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
            <div className="mt-2 text-xs text-gray-600 text-center">Max: {maxViews} visualizzazioni</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const DailyTable = ({ data }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg">Dettaglio Giornaliero (ultimi 10 giorni)</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-3">Data</th>
              <th className="text-right py-2 px-3">Visualizzazioni</th>
              <th className="text-right py-2 px-3">Carrello</th>
            </tr>
          </thead>
          <tbody>
            {data
              .slice(-10)
              .reverse()
              .map((day) => (
                <tr key={day.date} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-3">{day.date}</td>
                  <td className="text-right py-2 px-3">{day.views}</td>
                  <td className="text-right py-2 px-3">{day.cart_adds}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

/* ---- Main modal ---- */

export const AnalyticsModal = ({ open, onOpenChange, article, data, loading }) => {
  if (!open || !article) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-purple-600" />
            Statistiche Articolo
          </DialogTitle>
          <DialogDescription>{article.title}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
              <p className="text-gray-600">Caricamento statistiche...</p>
            </div>
          </div>
        ) : data ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KeyMetricCard Icon={Eye} iconClass="text-blue-600" value={data.total_views} label="Visualizzazioni Totali" />
              <KeyMetricCard Icon={Package} iconClass="text-green-600" value={data.total_cart_adds} label="Aggiunte al Carrello" />
              <KeyMetricCard Icon={Star} iconClass="text-orange-600" value={`${data.conversion_rate}%`} label="Tasso Conversione" />
              <KeyMetricCard Icon={Eye} iconClass="text-purple-600" value={data.views_last_7_days} label="Ultimi 7 Giorni" />
            </div>

            <StatusBadges data={data} />

            {data.daily_views && data.daily_views.length > 0 && <ViewsChart data={data.daily_views} />}
            {data.daily_views && data.daily_views.length > 0 && <DailyTable data={data.daily_views} />}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-600">Nessun dato disponibile</div>
        )}
      </DialogContent>
    </Dialog>
  );
};
